<template>
  <div v-if="isVisible" class="schema-field-item">

    <translatable-field
      v-if="field.translatable"
      :value="value"
      :label="label"
      :field-type="field.type"
      :locales="locales"
      :readonly="readonly"
      :entity-id="entityId"
      @input="$emit('input', $event)"
    />

    <source-field
      v-else-if="hasSource && !isArrayType"
      :value="value"
      :label="label"
      :field="field"
      :sibling-values="siblingValues"
      :entity-id="entityId"
      :readonly="readonly"
      @input="$emit('input', $event)"
    />

    <!-- ===== string + enum ===== -->
    <smart-select
      v-else-if="field.type === 'string' && field.enum"
      :value="value"
      :items="normalizedEnum"
      :label="label"
      :disabled="readonly"
      @input="$emit('input', $event)"
    />

    <!-- ===== string ===== -->
    <v-text-field
      v-else-if="field.type === 'string'"
      :value="value"
      :label="label"
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      @input="$emit('input', $event)"
    />

    <!-- ===== text ===== -->
    <v-textarea
      v-else-if="field.type === 'text'"
      :value="value"
      :label="label"
      rows="3"
      auto-grow
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      @input="$emit('input', $event)"
    />

    <!-- ===== integer ===== -->
    <v-text-field
      v-else-if="field.type === 'integer'"
      :value="value"
      :label="label"
      type="number"
      step="1"
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      @input="$emit('input', $event !== '' ? parseInt($event) : null)"
    />

    <!-- ===== number ===== -->
    <v-text-field
      v-else-if="field.type === 'number'"
      :value="value"
      :label="label"
      type="number"
      step="0.01"
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      @input="$emit('input', $event !== '' ? Number($event) : null)"
    />

    <!-- ===== boolean ===== -->
    <v-switch
      v-else-if="field.type === 'boolean'"
      :input-value="value"
      :label="label"
      dense
      hide-details
      class="mt-0 mb-4"
      :disabled="readonly"
      @change="$emit('input', !!$event)"
    />

    <!-- ===== image / file ===== -->
    <media-field
      v-else-if="field.type === 'image' || field.type === 'file'"
      :mode="field.type"
      :value="value"
      :label="label"
      :readonly="readonly"
      :entity-id="entityId"
      @input="$emit('input', $event)"
    />

    <array-field
      v-else-if="isArrayType"
      :value="value"
      :label="label"
      :field="field"
      :sibling-values="siblingValues"
      :entity-id="entityId"
      :readonly="readonly"
      @input="$emit('input', $event)"
    />

    <object-field
      v-else-if="field.type === 'object' && field.schema"
      :value="value || {}"
      :label="label"
      :schema="field.schema"
      :ref-name="field.$ref"
      :root-value="rootValue"
      :locales="locales"
      :readonly="readonly"
      :entity-id="entityId"
      @input="$emit('input', $event)"
    />

    <array-object-field
      v-else-if="field.type === 'array' && field.schema"
      :value="value || []"
      :label="label"
      :schema="field.schema"
      :ref-name="field.$ref"
      :root-value="rootValue"
      :locales="locales"
      :readonly="readonly"
      :entity-id="entityId"
      @input="$emit('input', $event)"
    />

    <!-- ===== object without schema (raw JSON) ===== -->
    <v-textarea
      v-else-if="field.type === 'object'"
      :value="JSON.stringify(value ?? {}, null, 2)"
      :label="label"
      :error-messages="rawJsonError"
      rows="3"
      auto-grow
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      style="font-family: monospace; font-size: 13px;"
      @input="onRawJsonInput"
    />

    <!-- ===== unknown fallback ===== -->
    <v-text-field
      v-else
      :value="typeof value === 'object' ? JSON.stringify(value) : value"
      :label="label + ' (' + field.type + ')'"
      dense
      outlined
      hide-details="auto"
      class="mb-4"
      :readonly="readonly"
      @input="$emit('input', $event)"
    />
  </div>
</template>

<script>
import fieldVisibility from '../mixins/fieldVisibility'
import MediaField from './fields/MediaField.vue'
import TranslatableField from './fields/TranslatableField.vue'
import SourceField from './fields/SourceField.vue'
import ArrayField from './fields/ArrayField.vue'
import ObjectField from './fields/ObjectField.vue'
import ArrayObjectField from './fields/ArrayObjectField.vue'
export default {
  name: 'SchemaFieldItem',
  components: {
    SmartSelect: () => import('../SmartSelect.vue'),
    MediaField,
    TranslatableField,
    SourceField,
    ArrayField,
    ObjectField,
    ArrayObjectField,
  },
  mixins: [fieldVisibility],
  props: {
    value: {
      default: null,
    },
    field: {
      type: Object,
      required: true,
    },
    fieldKey: {
      type: String,
      required: true,
    },
    siblingValues: {
      type: Object,
      default: () => ({}),
    },
    rootValue: {
      type: Object,
      required: true,
    },
    locales: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      rawJsonError: '',
    }
  },
  computed: {
    label() {
      return this.field.label || this.fieldKey
    },
    hasSource() {
      return !!this.field.source?.type
    },
    isArrayType() {
      return this.field.type === 'array:string' || this.field.type === 'array:int'
    },
    normalizedEnum() {
      if (!this.field.enum) {
        return []
      }
      return this.field.enum.map(item => {
        if (typeof item === 'string') {
          return { value: item, label: item }
        }
        return { value: item.value || '', label: item.label || item.value || '' }
      })
    },
    isVisible() {
      if (!this.field.depends_on || !this.field.depends_on.length) {
        return true
      }
      return this.matchesDependsOn(this.field.depends_on)
    },
  },
  methods: {
    onRawJsonInput(val) {
      try {
        const parsed = JSON.parse(val)
        this.rawJsonError = ''
        this.$emit('input', parsed)
      } catch (e) {
        this.rawJsonError = 'Invalid JSON'
      }
    },
  },
}
</script>

<style scoped>
  .schema-field-item > .v-card {
    border-color: #ccc;
  }
</style>
<style>
  .schema-field-item .v-chip--select {
    margin-top: 4px !important;
  }
</style>
